import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Selector múltiple del catálogo de ejercicios que la clienta NO puede/quiere hacer.
 * Controlado: `value` = array de IDs, `onChange(nextIds)`.
 * `testIdPrefix` conserva los data-testid históricos ("continuation") en ContinuationForm.
 */
export default function ExcludedExercisesSelector({ value = [], onChange, testIdPrefix = 'excluded' }) {
  const [catalogExercises, setCatalogExercises] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const tid = (suffix) => `${testIdPrefix}-excluded-exercises-${suffix}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const prof = await axios.get(`${API}/user/profile`, { headers, withCredentials: false }).catch(() => null);
        const rawGender = prof?.data?.gender || prof?.data?.user?.gender || '';
        const g = String(rawGender || '').toLowerCase();
        let param = '';
        if (['female', 'femenino', 'mujer', 'f'].includes(g)) param = '?gender=femenino';
        else if (['male', 'masculino', 'hombre', 'm'].includes(g)) param = '?gender=masculino';
        const res = await axios.get(`${API}/videos/for-user-form${param}`);
        if (!cancelled) setCatalogExercises(res.data?.exercises || []);
      } catch (e) {
        if (!cancelled) setCatalogExercises([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = exerciseSearch.trim().toLowerCase();
  const list = q
    ? catalogExercises.filter(ex =>
        (ex.title || '').toLowerCase().includes(q) ||
        (ex.muscle_group || '').toLowerCase().includes(q))
    : catalogExercises;
  const groups = {};
  list.forEach(ex => {
    const k = ex.muscle_group || 'Otros';
    (groups[k] = groups[k] || []).push(ex);
  });

  return (
    <div
      className="rounded-xl border p-3"
      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
      data-testid={`${testIdPrefix}-excluded-exercises-selector`}
    >
      <input
        type="text"
        placeholder="Buscar en el catálogo…"
        className="w-full mb-2 px-3 py-2 rounded-md text-white text-sm placeholder-gray-500 focus:outline-none"
        style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
        value={exerciseSearch}
        onChange={(e) => setExerciseSearch(e.target.value)}
        data-testid={tid('search')}
      />
      <div className="max-h-56 overflow-y-auto pr-1">
        {catalogLoading && (
          <p className="text-xs text-gray-400 py-2 text-center">Cargando catálogo…</p>
        )}
        {!catalogLoading && catalogExercises.length === 0 && (
          <p className="text-xs text-gray-400 py-2 text-center">No hay ejercicios en el catálogo por ahora.</p>
        )}
        {!catalogLoading && Object.entries(groups).map(([mg, exs]) => (
          <div key={mg} className="mb-1">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-2 mb-1">{mg}</div>
            {exs.map(ex => {
              const checked = value.includes(ex.id);
              return (
                <label
                  key={ex.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                  data-testid={`${testIdPrefix}-excluded-exercise-option-${ex.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? value.filter(x => x !== ex.id) : [...value, ex.id])}
                    className="accent-pink-500"
                  />
                  <span className="text-sm text-white">{ex.title}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {value.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-300">{value.length} ejercicio(s) seleccionado(s)</span>
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-pink-300 hover:text-pink-200 underline"
            data-testid={tid('clear')}
          >
            Limpiar
          </button>
        </div>
      )}
    </div>
  );
}
